import React, {
	ChangeEvent, ClipboardEvent, FC, KeyboardEvent, useCallback, useEffect, useMemo,
} from 'react';

import {
	AsyncCellSearch,
} from '@aleph/shared-components';
import {
	ICellWithColumnEditor,
} from '@aleph/supergrid';
import {
	CommunityOptionsQuery,
	CommunityOptionsQueryItems,
	GroupSuperGridListQueryItems,
	RedditCommunityLabelsQuery, RedditCommunityLabelsQueryVariables,
} from 'wb-reddit/graphql/generated';
import { debounce, uniqBy } from 'lodash';
import { useLazyQuery } from '@apollo/client';
import redditCommunityLabelsQuery from 'wb-reddit/app/routes/superGrid/targeting/api/communityLabels.graphql';

const CommunitiesCellEditor: FC<ICellWithColumnEditor<CommunityOptionsQueryItems[], GroupSuperGridListQueryItems>> = (
	params,
) => {
	const initialValue = useMemo(() => params.value, []);
	const {
		data, value, setValue, editorContext, setEditorContext,
	} = params;

	const [getCommunityLabels, { loading }] = useLazyQuery<RedditCommunityLabelsQuery, RedditCommunityLabelsQueryVariables>(redditCommunityLabelsQuery);

	const changeSearch = useCallback(debounce((e: ChangeEvent<HTMLInputElement>) => {
		setEditorContext({
			...editorContext,
			search: e.target.value,
		});
	}, 300), [editorContext]);

	const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			params.stopEditing();
		}

		if (e.key === 'Escape') {
			setValue(initialValue);

			setTimeout(() => params.stopEditing());
		}
	};

	useEffect(() => {
		setEditorContext({
			...editorContext,
			adAccountId: data?.parent?.parent?.adAccount?.adAccountId,
		});
	}, [data]);

	const onPaste = async (e: ClipboardEvent) => {
		e.stopPropagation();
		e.preventDefault();

		const communityIds = e?.clipboardData?.getData?.('text')?.split?.('\n')?.filter?.(text => !!text) ?? [];

		try {
			const { data: communityData } = await getCommunityLabels({
				variables: {
					communityIds,
				},
			});

			setValue(
				uniqBy(
					[
						...(value ?? []),
						...(communityData?.targetingCommunities?.items ?? []),
					] as CommunityOptionsQuery['options']['items'],
					'value',
				),
			);
		} catch (err) {
			console.error(err);
		}
	};

	return (
		<AsyncCellSearch onChange={changeSearch}
			onKeyDown={onKeyDown}
			onPaste={onPaste}
			loading={loading}
			loaderText="Pasting..."
			placeholder='Search communities...'
			onSubmit={() => params.stopEditing()}
			onCancel={() => params.api.stopEditing(true)}
		/>
	);
};

export default CommunitiesCellEditor;
